import {useMutation, useQueryClient} from "@tanstack/react-query";
import useAbility from "./useAbility";

import {
    createCommentLike,
    createLike,
    createSubCommentLike,
    deleteCommentLike,
    deleteLike, deleteSubCommentLike,
    createForumTopicCommentLike,
    deleteForumTopicCommentLike,
    createTopicLike,
    deleteTopicLike
} from "../api/public-voissa/likes";
import {MEDIA_KEY} from "../api/public-voissa/medias";
import useCurrentUserActionLocked, {ACTION_LOCKED} from "./useCurrentUserActionLocked";
import moment from "moment-timezone";
import {HOME_FEED_FOR_YOU_KEY, HOME_FEED_KEY, HOME_FEED_MEDIAS_KEY} from "../api/public-voissa/home-feed";
import {USER_FIL_ACTUALITY_KEY} from "../api/public-voissa/users";
import {ARTICLE_KEY} from "../api/public-voissa/articles";
import {TOPIC_KEY} from "../api/public-voissa/topics";
import {COMMENTS_KEY} from "../api/public-voissa/comments";
import {FORUM_TOPIC_COMMENTS_KEY} from "../api/public-voissa/forums";


const LIKE_KINDS = {
    MEDIA : "medias",
    STATUS : "status",
    ARTICLE : "articles",
    COMMENT : "comments",
    SUB_COMMENT : "sub_comments",
    TOPIC: "topic",
    TOPIC_COMMENT: "topiccomment"
}

const useLike = ({
    parentKind  = "",
    commentKindParent,
    parentId,
    commentId,
    topicId,
    forumId,
    subCommentId,
    isLike = false,
    contentOwner,
    onSuccess = () =>{},
    onMutate = () =>{},
    onError = () =>{}
}) =>{

    const queryClient = useQueryClient();
    const {can} = useAbility();

    const canLike =  can({ action:'create', subject:'Like' });


    function updateCommentCount(increase) {
        const increment = increase ? 1 : -1;
        const updateItemCounts = (item) => {
            if ((item._id.toString() === parentId?.toString()) || (item._id.toString() === commentId?.toString()) || (item._id.toString() === subCommentId?.toString())) {
                return {
                    ...item,
                    count: {
                        ...item.count,
                        like: item.count.like + increment
                    },
                    isLiked : increase,
                    updatedAt: parentKind?.toLowerCase() === LIKE_KINDS.TOPIC_COMMENT ? item.updatedAt : moment()
                };
            }
            return item;
        };

        const updatePagesData = (pages) => pages.map(page => ({
            ...page,
            data: page.data.map(updateItemCounts)
        }));

        const updateDataInCache = (key) => {
            const previousData = queryClient.getQueryData(key);
            if (previousData?.pages) {
                const newData = {
                    ...previousData,
                    pages: updatePagesData(previousData.pages),
                    pageParams: [...previousData.pageParams]
                };
                queryClient.setQueryData(key, newData);
            } else if (Array.isArray(previousData?.data)) {
                if (previousData) {
                    const newData = {
                        ...previousData,
                        data: previousData.data.map(updateItemCounts)
                    };
                    queryClient.setQueryData(key, newData);
                }
            } else if(previousData) {
                const updatedData = {
                    ...previousData,
                    count: {
                        ...previousData.count,
                        like: previousData.count.like + increment
                    },
                    isLiked : increase
                };

                queryClient.setQueryData(key, updatedData);
            }
        };

        // Handle updates based on the type of parent
        if (parentKind === LIKE_KINDS.MEDIA) {
            updateDataInCache([HOME_FEED_KEY]);
            updateDataInCache([HOME_FEED_FOR_YOU_KEY]);

            queryClient.getQueryCache().findAll([HOME_FEED_MEDIAS_KEY]).forEach(query => {
                updateDataInCache(query.queryKey);
            });
            queryClient.getQueryCache().findAll([MEDIA_KEY, parentId]).forEach(query => {
                updateDataInCache(query.queryKey);
            });
        }
        if (parentKind === LIKE_KINDS.ARTICLE) {
            updateDataInCache([HOME_FEED_KEY]);
            updateDataInCache([HOME_FEED_FOR_YOU_KEY]);
            queryClient.getQueryCache().findAll([ARTICLE_KEY, parentId]).forEach(query => {
                updateDataInCache(query.queryKey);
            });
        }
        if (parentKind === LIKE_KINDS.STATUS) {
            updateDataInCache([HOME_FEED_KEY]);
            updateDataInCache([HOME_FEED_FOR_YOU_KEY]);
            updateDataInCache([USER_FIL_ACTUALITY_KEY, parentId]);
            updateDataInCache([USER_FIL_ACTUALITY_KEY, contentOwner?._id]);

        }
        if (parentKind === LIKE_KINDS.TOPIC) {
            updateDataInCache([TOPIC_KEY, parentId]);
        }
        if (parentKind?.toLowerCase() === LIKE_KINDS.TOPIC_COMMENT) {
            queryClient.getQueryCache().findAll([FORUM_TOPIC_COMMENTS_KEY, forumId, topicId]).forEach(query => {
                updateDataInCache(query.queryKey);
            });
        }
        if (parentKind === LIKE_KINDS.COMMENT) {
            updateDataInCache([COMMENTS_KEY, parentId]);
        }
        if (parentKind === LIKE_KINDS.SUB_COMMENT) {
            updateDataInCache([COMMENTS_KEY, parentId, commentId]);
        }
    }

    async function handleSuccess (){
        onSuccess();
    }

    function handleError (){
        //setIsLiked(!isLiked);
        updateCommentCount(!isLike);
        onError();
    }

    const getMutation = (isLiked) =>{
        const _parentKind = parentKind?.toLowerCase();
        const _commentKindParent = commentKindParent?.toLowerCase();
        switch (_parentKind) {
            case LIKE_KINDS.MEDIA:
                return isLiked ? deleteLike(_parentKind, parentId) : createLike(_parentKind, parentId);
            case LIKE_KINDS.ARTICLE:
                return isLiked ? deleteLike(_parentKind, parentId) : createLike(_parentKind, parentId);
            case LIKE_KINDS.STATUS:
                return isLiked ? deleteLike(_parentKind, parentId) : createLike(_parentKind, parentId);
            case LIKE_KINDS.COMMENT:
                return isLiked ? deleteCommentLike(_commentKindParent, parentId, commentId) : createCommentLike(_commentKindParent, parentId, commentId);
            case LIKE_KINDS.SUB_COMMENT:
                return isLiked ? deleteSubCommentLike(_commentKindParent, parentId, commentId, subCommentId) : createSubCommentLike(_commentKindParent, parentId, commentId, subCommentId);
            case LIKE_KINDS.TOPIC:
                return isLiked ? deleteTopicLike(parentId) : createTopicLike(parentId);
            case LIKE_KINDS.TOPIC_COMMENT:
                return isLiked ? deleteForumTopicCommentLike(forumId,topicId, parentId) : createForumTopicCommentLike(forumId,topicId, parentId);
            default:
                return null;
        }
    }

    const {
        mutate, isLoading
    } = useMutation(getMutation,{
        onSuccess: handleSuccess,
        onError: handleError
    });

    const {handleLockedAction} = useCurrentUserActionLocked({action : ACTION_LOCKED.CREATE_LIKE, contentOwner});

    function handleLike() {
        if(!isLoading){
            if(canLike){
                //setIsLiked(!isLiked);
                updateCommentCount(!isLike);
                mutate(isLike);
                onMutate();
            }
            else {
                /*switch (currentUser?.role) {
                    case USER_ROLES.EMAIL_UNCONFIRMED:
                        openModal('EmailValidation', {toggle : true, isModal : true});
                        break;
                    default :
                        //openModal('EncourageSignup');
                        navigate(createLink("signin"));
                }*/
                handleLockedAction();
            }
        }
    }

    return {isLiked : isLike, handleLike};
}

export default useLike;
export {LIKE_KINDS};